import { Modal } from "antd";
import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { registerExclusiveBlockingModal } from "components/Modal/exclusiveBlockingModal";
import { ReactNode, useState } from "react";
import "./showConfirmModal.scss";

interface ModalButtonProps {
    className?: string;
    disabled?: boolean;
}

/**
 * Colour of the confirm button, per ui-design-principle-modals-dialogs §3.1:
 * `primary` (blue) for a single dominant action, `dark` for co-equal actions,
 * `danger` (red) for a destructive confirm.
 */
export type ConfirmVariation = "primary" | "dark" | "danger";

interface ConfirmModalProps {
    title?: ReactNode;
    content?: ReactNode;
    icon?: ReactNode | null;
    onConfirm: () => void | Promise<unknown>;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    confirmVariation?: ConfirmVariation;
    // Kept for call-site compatibility: only `disabled` and the variation implied
    // by a `button--danger` / `button--dark` class are read.
    cancelButtonProps?: ModalButtonProps;
    confirmButtonProps?: ModalButtonProps;
    className: string;
    centered?: boolean;
    zIndex?: number;
    width?: number;
    // Which footer button receives focus when the dialog opens. Defaults to
    // `null` (neither): the shared `.button` treats `:focus` like `:hover`, so an
    // auto-focused confirm button would sit in its hover colour at rest.
    autoFocusButton?: "ok" | "cancel" | null;
    // When true, this dialog registers as an "exclusive" blocking modal for its
    // whole lifetime (see exclusiveBlockingModal). While it is open, the
    // unsaved-changes navigation guard suppresses its own popover so the two
    // never stack — only one warning dialog is ever shown at a time. Use for
    // blocking data-safety confirmations (e.g. the stale-write conflict dialog).
    exclusive?: boolean;
    // Fires after the dialog has fully closed (antd afterClose). Preserved when
    // `exclusive` is set (the registry release is chained in front of it).
    afterClose?: () => void;
}

export const variationFromLegacyClassName = (className?: string): ConfirmVariation => {
    if (className?.includes("button--danger")) {
        return "danger";
    }
    if (className?.includes("button--dark")) {
        return "dark";
    }
    return "primary";
};

interface ConfirmModalFooterProps {
    confirmText: string;
    cancelText: string;
    confirmVariation: ConfirmVariation;
    confirmDisabled?: boolean;
    cancelDisabled?: boolean;
    autoFocusButton: "ok" | "cancel" | null;
    onConfirm: () => void | Promise<unknown>;
    onCancel: () => void;
}

/**
 * The canonical dialog button row: Cancel left, confirm right, rendered with the
 * shared `<CancelButton>` / `<Button>` components.
 */
export const ConfirmModalFooter = ({
    confirmText,
    cancelText,
    confirmVariation,
    confirmDisabled,
    cancelDisabled,
    autoFocusButton,
    onConfirm,
    onCancel,
}: ConfirmModalFooterProps) => {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        if (loading) {
            return;
        }
        const result = onConfirm();
        if (result && typeof (result as Promise<unknown>).then === "function") {
            setLoading(true);
            try {
                await result;
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="confirmModalFooter">
            <CancelButton
                label={cancelText}
                onClick={onCancel}
                disabled={cancelDisabled || loading}
                autoFocus={autoFocusButton === "cancel"}
            />
            <Button
                label={confirmText}
                variation={confirmVariation}
                onClick={handleConfirm}
                disabled={confirmDisabled}
                loading={loading}
                autoFocus={autoFocusButton === "ok"}
                className="confirmModalFooter__confirm"
            />
        </div>
    );
};

export const showConfirmModal = ({
    title,
    content,
    icon = null,
    onConfirm = () => {},
    onCancel = () => {},
    confirmText = "",
    cancelText = "",
    confirmVariation,
    cancelButtonProps,
    confirmButtonProps,
    className,
    centered,
    zIndex,
    width,
    autoFocusButton = null,
    exclusive = false,
    afterClose,
}: ConfirmModalProps) => {
    const release = exclusive ? registerExclusiveBlockingModal() : null;
    let handle: ReturnType<typeof Modal.confirm> | undefined;

    const close = () => handle?.destroy();
    const handleCancel = () => {
        onCancel();
        close();
    };
    const handleConfirm = () => {
        const result = onConfirm();
        if (result && typeof (result as Promise<unknown>).then === "function") {
            // Keep the dialog open (confirm button shows a spinner) until the
            // action settles; a rejected action leaves it open for a retry.
            return (result as Promise<unknown>).then(close);
        }
        close();
        return undefined;
    };

    handle = Modal.confirm({
        title,
        content: (
            <>
                {content}
                <ConfirmModalFooter
                    confirmText={confirmText || "OK"}
                    cancelText={cancelText || "Cancel"}
                    confirmVariation={
                        confirmVariation ??
                        variationFromLegacyClassName(confirmButtonProps?.className)
                    }
                    confirmDisabled={confirmButtonProps?.disabled}
                    cancelDisabled={cancelButtonProps?.disabled}
                    autoFocusButton={autoFocusButton}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            </>
        ),
        icon,
        // Esc dismisses the dialog through antd; that path still reports a cancel.
        onCancel,
        className: `${className} sharedFooterConfirmModal`,
        centered,
        zIndex,
        width,
        autoFocusButton: null,
        afterClose: () => {
            release?.();
            afterClose?.();
        },
    });
    return handle;
};
